/**
 * Cliente HTTP del admin backoffice de billing (ADR-013, S4-E).
 *
 *   GET  /api/v1/admin/billing/payments?status=...
 *   GET  /api/v1/admin/billing/payments/:id
 *   POST /api/v1/admin/billing/payments/:id/mark-paid
 *   GET  /api/v1/admin/billing/subscriptions
 */
import { apiRequest } from './client.js';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'disputed';
export type PaymentProvider = 'paypal' | 'azul' | 'cardnet' | 'transfer' | 'manual';

export interface PaymentListItem {
  id: string;
  tenant_id: string;
  tenant_name: string;
  amount: number;
  currency: string;
  provider: PaymentProvider;
  status: PaymentStatus;
  due_at: string;
  paid_at: string | null;
  external_reference: string | null;
}

export interface PaymentEvent {
  id: string;
  event_type: string;
  actor_kind: string;
  actor_id: string | null;
  payload: unknown;
  created_at: string;
}

export interface PaymentDetail extends PaymentListItem {
  subscription: {
    id: string;
    plan_code: string;
    status: string;
  };
  voucher_url: string | null;
  marked_paid_at: string | null;
  marked_paid_by: string | null;
  notes: string | null;
  events: PaymentEvent[];
}

export interface MarkPaidBody {
  external_reference: string;
  voucher_url?: string;
  notes?: string;
}

export interface UploadInvoiceBody {
  payment_id: string;
  invoice_pdf_url: string;
  ncf?: string;
  notes?: string;
}

export interface SubscriptionListItem {
  id: string;
  tenant_id: string;
  tenant_name: string;
  plan_code: string;
  status: string;
  billing_cycle: string;
  current_period_end: string;
  trial_ends_at: string | null;
  external_provider: string | null;
}

interface ListPaymentsQuery {
  status?: PaymentStatus;
  provider?: PaymentProvider;
  tenantId?: string;
  limit?: number;
  offset?: number;
}

function buildQuery(q: ListPaymentsQuery): string {
  const parts: string[] = [];
  if (q.status) parts.push(`status=${encodeURIComponent(q.status)}`);
  if (q.provider) parts.push(`provider=${encodeURIComponent(q.provider)}`);
  if (q.tenantId) parts.push(`tenantId=${encodeURIComponent(q.tenantId)}`);
  if (q.limit !== undefined) parts.push(`limit=${q.limit}`);
  if (q.offset !== undefined) parts.push(`offset=${q.offset}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const adminBillingApi = {
  listPayments: (q: ListPaymentsQuery = {}) =>
    apiRequest<{ payments: PaymentListItem[]; total: number }>(
      `/api/v1/admin/billing/payments${buildQuery(q)}`,
    ),

  getPayment: (id: string) => apiRequest<PaymentDetail>(`/api/v1/admin/billing/payments/${id}`),

  markPaid: (id: string, body: MarkPaidBody) =>
    apiRequest<PaymentDetail>(`/api/v1/admin/billing/payments/${id}/mark-paid`, {
      method: 'POST',
      body,
    }),

  listSubscriptions: () =>
    apiRequest<{ subscriptions: SubscriptionListItem[] }>('/api/v1/admin/billing/subscriptions'),

  uploadInvoice: (subscriptionId: string, body: UploadInvoiceBody) =>
    apiRequest<{ payment_event_id: string; message: string }>(
      `/api/v1/admin/billing/subscriptions/${subscriptionId}/upload-invoice`,
      { method: 'POST', body },
    ),
};
